import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { KeyRound, Sparkles, Camera, Landmark, ArrowRight, Phone, Plus, TrendingUp, BadgeCheck } from "lucide-react";
import { PlatformLayout } from "./PlatformLayout";
import { Reveal } from "./Reveal";
import { AnimatedTitle } from "./AnimatedTitle";
import { PortalBanner } from "./PortalBanner";
import { useTranslation } from "react-i18next";
import { VINCENT_CASUAL } from "./api";

const HERO = "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/6ff9668b9e7c30c16314a3516f29a7bacf9056ce994bfc430693a36680a85e7b.jpeg";

const SERVICES = [
  { icon: TrendingUp, title: "Analyse et stratégie de marché", badge: "Offerte", text: "Une étude complète de votre marché local et une stratégie de commercialisation sur-mesure, sans aucun engagement de votre part.", img: "https://images.unsplash.com/photo-1758523671285-9ff3f4e0ff38?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { icon: BadgeCheck, title: "Une estimation juste et argumentée", text: "Un prix cohérent, fondé sur des données réelles et le vrai potentiel de votre bien — jamais surévalué pour séduire.", img: "https://images.unsplash.com/photo-1626178793926-22b28830aa30?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { icon: KeyRound, title: "Vente off-market", text: "Une commercialisation confidentielle auprès d'un réseau d'acquéreurs qualifiés, avant toute diffusion publique.", img: "https://images.unsplash.com/photo-1706164971302-e30c0640cc3b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { icon: Camera, title: "Valorisation & photographie", text: "Reportage photo premium, mise en scène et présentation immersive pour révéler chaque bien.", img: "https://images.unsplash.com/photo-1780191751680-cdd89eef7b27?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { icon: Sparkles, title: "Home staging & projection IA", text: "Des projections de rénovation et d'aménagement pour aider les acquéreurs à se projeter.", img: "https://images.unsplash.com/photo-1769063238167-d00e112147c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
  { icon: Landmark, title: "Conseil patrimonial & financement", text: "Un accompagnement sur les aspects financiers, notariaux et patrimoniaux de votre projet.", img: "https://images.unsplash.com/photo-1521791055366-0d553872125f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" },
];

const STEPS = [
  { n: "01", title: "Prise de contact", text: "Un premier échange pour comprendre votre projet et vos attentes." },
  { n: "02", title: "Estimation & stratégie", text: "Analyse du bien, estimation et définition d'une stratégie de commercialisation." },
  { n: "03", title: "Valorisation", text: "Reportage, mise en valeur et présentation immersive du bien." },
  { n: "04", title: "Commercialisation off-market", text: "Diffusion ciblée et confidentielle auprès des bons acquéreurs." },
  { n: "05", title: "Négociation & signature", text: "Accompagnement jusqu'à la signature, en toute sérénité." },
];

export default function Services() {
  const [openStep, setOpenStep] = useState("01");
  const { t } = useTranslation();
  return (
    <PlatformLayout>
      {/* HERO — bannière de couverture (format sites personnalisés) */}
      <PortalBanner
        image={HERO}
        testid="services-hero"
        subtitle={t("expertises.hero_subtitle")}
        title={t("expertises.hero_title")}
      />

      {/* Services grid */}
      <section className="py-20 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-10">
        <Reveal className="max-w-2xl">
          <span className="calli text-3xl sm:text-4xl text-[#C19A6B]">{t("expertises.grid_eyebrow")}</span>
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mt-2 text-zinc-900">{t("expertises.grid_title")}</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
          {SERVICES.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }} data-testid={`service-card-${i}`} className="group relative overflow-hidden bg-zinc-100 border border-zinc-200 rounded-[5px] p-8 hover:border-[#C19A6B]/50 hover:shadow-xl hover:shadow-black/5 transition-all">
              <div className="absolute inset-0 bg-cover bg-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-out" style={{ backgroundImage: `url(${s.img})` }} />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "linear-gradient(to top, rgba(5,5,5,0.92), rgba(5,5,5,0.72))" }} />
              <div className="relative z-10 flex flex-col items-center text-center space-y-3">
                <s.icon className="w-7 h-7 text-[#C19A6B]" />
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <h3 className="text-lg font-semibold text-zinc-900 group-hover:text-white transition-colors">{t(`expertises.services.${i}.title`)}</h3>
                  {s.badge && <span className="text-[10px] uppercase tracking-wider bg-[#C19A6B] text-white px-2 py-0.5 rounded-[3px]">{t("expertises.badge_offerte")}</span>}
                </div>
                <p className="text-sm font-light text-zinc-600 group-hover:text-white leading-relaxed transition-colors">{t(`expertises.services.${i}.text`)}</p>
              </div>
            </motion.div>
          ))}
        </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#0B0B0D] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-10">
          <Reveal className="max-w-2xl">
            <span className="calli text-lg text-[#C19A6B]">{t("expertises.method_eyebrow")}</span>
            <AnimatedTitle as="h2" className="text-3xl sm:text-4xl font-light tracking-tight mt-2" text={t("expertises.method_title")} />
          </Reveal>
          <div className="space-y-3" data-testid="services-steps">
            {STEPS.map((s, i) => {
              const open = openStep === s.n;
              return (
                <motion.button
                  key={s.n}
                  type="button"
                  data-testid={`step-${s.n}`}
                  onClick={() => setOpenStep(open ? null : s.n)}
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className={`w-full text-left grid grid-cols-12 gap-4 items-center p-6 border transition-all duration-300 ${open ? "bg-[#C19A6B]/10 border-[#C19A6B]/50" : "bg-[#111114] border-white/10 hover:border-[#C19A6B]/30"}`}
                >
                  <div className={`col-span-2 sm:col-span-1 text-3xl font-light transition-colors ${open ? "text-[#C19A6B]" : "text-white/40"}`}>{s.n}</div>
                  <div className="col-span-8 sm:col-span-9">
                    <div className="text-lg font-light text-white">{t(`expertises.steps.${i}.title`)}</div>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.p
                          initial={{ height: 0, opacity: 0, marginTop: 0 }}
                          animate={{ height: "auto", opacity: 1, marginTop: 8 }}
                          exit={{ height: 0, opacity: 0, marginTop: 0 }}
                          transition={{ duration: 0.3 }}
                          className="text-sm font-light text-zinc-400 leading-relaxed overflow-hidden"
                        >
                          {t(`expertises.steps.${i}.text`)}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                  <div className="col-span-2 flex justify-end">
                    <Plus className={`w-5 h-5 text-[#C19A6B] transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
                  </div>
                </motion.button>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-28 overflow-hidden" data-testid="services-cta">
        <div className="absolute inset-0 bg-cover" style={{ backgroundImage: `url(${VINCENT_CASUAL})`, backgroundPosition: "center 25%" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(5,5,5,0.94), rgba(5,5,5,0.75))" }} />
        <motion.div initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="relative z-10 max-w-4xl mx-auto px-6 text-center space-y-6">
          <AnimatedTitle as="h2" className="text-3xl sm:text-4xl font-light tracking-tight text-white" text={t("expertises.cta_title")} />
          <p className="text-zinc-200 font-light">{t("expertises.cta_text")}</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="tel:+33688675357" className="inline-flex flex-col items-center leading-tight bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider px-8 py-3 transition-colors">
              <span className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4" /> {t("expertises.cta_btn")}</span>
              <span className="text-[10px] tracking-[0.25em] opacity-80">{t("expertises.badge_offerte")}</span>
            </a>
            <Link to="/biens-off-market" className="inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white font-medium uppercase tracking-wider text-sm px-8 py-4 transition-colors">{t("expertises.cta_see")} <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </motion.div>
      </section>
    </PlatformLayout>
  );
}
